import {
  normalizeProfileSettings
} from "../settings/profileSettings.js";
import {
  normalizeAppSettings
} from "../settings/appSettings.js";
import {
  normalizeExpenseType
} from "../expenseType.js";
import {
  CURRENT_YEAR,
  DEFAULT_PROFILE_ID,
  ensureSeedData,
  getMetaValue,
  openDatabase,
  PROFILE_BACKUP_FORMAT_VERSION,
  readAllFromStore,
  requestToPromise,
  STORE_BUDGET_CATEGORIES,
  STORE_EXPENSES,
  STORE_PROFILES,
  STORE_PROFILE_SETTINGS,
  STORE_YEARS,
  transactionDone
} from "./walletWatchDbCore.js";
import {
  normalizeProfileRecord,
  setActiveBudgetScope
} from "./walletWatchProfiles.js";

function getNormalizedBackupYearSet(payload) {
  const years = new Set();

  [payload.activeYear]
    .concat(payload.years?.map((entry) => entry?.year) ?? [])
    .concat(payload.budgetCategories?.map((entry) => entry?.year) ?? [])
    .concat(payload.expenses?.map((entry) => entry?.year) ?? [])
    .forEach((value) => {
      const year = String(value ?? "").trim();

      if (year) {
        years.add(year);
      }
    });

  return years;
}

function validateProfileBackupPayload(payload) {
  if (!payload || typeof payload !== "object") {
    throw new Error("Backup file is not a valid JSON object.");
  }

  if (payload.kind !== "wallet-watch-profile-backup") {
    throw new Error("Backup file is not a wallet-watch profile backup.");
  }

  if (payload.formatVersion !== PROFILE_BACKUP_FORMAT_VERSION) {
    throw new Error(`Backup format version ${payload.formatVersion ?? "unknown"} is not supported.`);
  }

  if (!payload.profile || typeof payload.profile !== "object") {
    throw new Error("Backup file is missing profile information.");
  }

  const profileName = String(payload.profile.name ?? "").trim();
  if (!profileName) {
    throw new Error("Backup file profile name is missing.");
  }

  if (!Array.isArray(payload.years)) {
    throw new Error("Backup file is missing the years list.");
  }

  if (!Array.isArray(payload.budgetCategories)) {
    throw new Error("Backup file is missing the budget category list.");
  }

  if (!Array.isArray(payload.expenses)) {
    throw new Error("Backup file is missing the expenses list.");
  }

  const yearSet = getNormalizedBackupYearSet(payload);
  if (yearSet.size === 0) {
    throw new Error("Backup file does not include any usable year.");
  }

  return {
    profileName,
    profileSettings: normalizeProfileSettings(payload.profileSettings),
    appSettings: normalizeAppSettings(payload.appSettings),
    activeYear: yearSet.has(String(payload.activeYear ?? "").trim())
      ? String(payload.activeYear ?? "").trim()
      : Array.from(yearSet).sort((left, right) => String(right).localeCompare(String(left)))[0],
    years: Array.from(yearSet).sort((left, right) => String(left).localeCompare(String(right))),
    budgetCategories: payload.budgetCategories,
    expenses: payload.expenses
  };
}

function buildRestoredProfileName(baseName, existingNames) {
  const trimmedBaseName = String(baseName ?? "").trim() || "Restored Profile";
  const directCandidate = `${trimmedBaseName} (Restored)`;

  if (!existingNames.has(directCandidate.toLocaleLowerCase())) {
    return directCandidate;
  }

  let suffix = 2;
  while (existingNames.has(`${directCandidate} ${suffix}`.toLocaleLowerCase())) {
    suffix += 1;
  }

  return `${directCandidate} ${suffix}`;
}

async function getAppSettings() {
  const [language, theme] = await Promise.all([
    getMetaValue("language"),
    getMetaValue("theme")
  ]);
  return normalizeAppSettings({ language, theme });
}

function getRestoredExpenseCategoryId({
  categoryIdsByLegacyId,
  categoryIdsByYearName,
  categoryName,
  expense,
  year
}) {
  return categoryIdsByLegacyId.get(String(expense?.categoryId ?? ""))
    ?? categoryIdsByYearName.get(`${year}::${categoryName.toLocaleLowerCase()}`)
    ?? "";
}

function buildRestoredExpenseRecord({
  categoryId,
  categoryName,
  expense,
  now,
  profileId,
  year
}) {
  return {
    id: crypto.randomUUID(),
    profileId,
    year,
    date: String(expense?.date ?? "").trim(),
    amount: Number(expense?.amount ?? 0),
    method: String(expense?.method ?? "").trim(),
    type: normalizeExpenseType(expense?.type),
    supplier: String(expense?.supplier ?? "").trim(),
    categoryId,
    category: categoryName,
    service: String(expense?.service ?? "").trim(),
    createdAt: expense?.createdAt ?? now,
    updatedAt: expense?.updatedAt ?? now
  };
}

function restoreExpenseRecord({
  categoryIdsByLegacyId,
  categoryIdsByYearName,
  expense,
  expensesStore,
  now,
  profileId,
  yearIdsByYear
}) {
  const year = String(expense?.year ?? "").trim();
  if (!yearIdsByYear.has(year)) {
    return;
  }

  const categoryName = String(expense?.category ?? "").trim();
  const categoryId = getRestoredExpenseCategoryId({
    categoryIdsByLegacyId,
    categoryIdsByYearName,
    categoryName,
    expense,
    year
  });

  expensesStore.put(buildRestoredExpenseRecord({
    categoryId,
    categoryName,
    expense,
    now,
    profileId,
    year
  }));
}

export async function exportActiveProfileBackup() {
  await ensureSeedData();
  const [activeProfileId, activeYear, appSettings] = await Promise.all([
    getMetaValue("activeProfileId"),
    getMetaValue("activeYear"),
    getAppSettings()
  ]);
  const profileId = activeProfileId ?? DEFAULT_PROFILE_ID;
  const year = activeYear ?? CURRENT_YEAR;
  const db = await openDatabase();
  const transaction = db.transaction(
    [
      STORE_PROFILES,
      STORE_PROFILE_SETTINGS,
      STORE_YEARS,
      STORE_BUDGET_CATEGORIES,
      STORE_EXPENSES
    ],
    "readonly"
  );
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const profileSettingsStore = transaction.objectStore(STORE_PROFILE_SETTINGS);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const budgetCategoriesStore = transaction.objectStore(STORE_BUDGET_CATEGORIES);
  const expensesStore = transaction.objectStore(STORE_EXPENSES);

  const [profile, profileSettingsRecord, years, budgetCategories, expenses] = await Promise.all([
    requestToPromise(profilesStore.get(profileId)),
    requestToPromise(profileSettingsStore.get(profileId)),
    readAllFromStore(yearsStore),
    readAllFromStore(budgetCategoriesStore),
    readAllFromStore(expensesStore)
  ]);
  await transactionDone(transaction);

  if (!profile) {
    throw new Error(`Active profile "${profileId}" could not be found for backup export.`);
  }

  return {
    kind: "wallet-watch-profile-backup",
    formatVersion: PROFILE_BACKUP_FORMAT_VERSION,
    exportedAt: new Date().toISOString(),
    appSettings,
    activeProfileId: profileId,
    activeYear: year,
    profile,
    profileSettings: normalizeProfileSettings(profileSettingsRecord?.settings),
    years: years.filter((entry) => entry.profileId === profileId),
    budgetCategories: budgetCategories.filter((entry) => entry.profileId === profileId),
    expenses: expenses.filter((entry) => entry.profileId === profileId)
  };
}

export async function restoreProfileBackup(payload, options = {}) {
  await ensureSeedData();
  const backup = validateProfileBackupPayload(payload);
  const db = await openDatabase();
  const transaction = db.transaction(
    [
      STORE_PROFILES,
      STORE_PROFILE_SETTINGS,
      STORE_YEARS,
      STORE_BUDGET_CATEGORIES,
      STORE_EXPENSES
    ],
    "readwrite"
  );
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const profileSettingsStore = transaction.objectStore(STORE_PROFILE_SETTINGS);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const budgetCategoriesStore = transaction.objectStore(STORE_BUDGET_CATEGORIES);
  const expensesStore = transaction.objectStore(STORE_EXPENSES);
  const existingProfiles = await readAllFromStore(profilesStore);
  const existingNames = new Set(
    existingProfiles
      .filter((profile) => !profile.archivedAt)
      .map((profile) => String(profile.name ?? "").trim().toLocaleLowerCase())
      .filter(Boolean)
  );
  const now = new Date().toISOString();
  const profileId = `profile-${crypto.randomUUID()}`;
  const profileName = buildRestoredProfileName(backup.profileName, existingNames);
  const yearIdsByYear = new Map();
  const categoryIdsByLegacyId = new Map();
  const categoryIdsByYearName = new Map();

  profilesStore.put({
    id: profileId,
    name: profileName,
    source: options.source ?? "restore",
    isDefault: false,
    archivedAt: null,
    createdAt: now,
    updatedAt: now
  });

  profileSettingsStore.put({
    profileId,
    settings: backup.profileSettings,
    createdAt: now,
    updatedAt: now
  });

  backup.years.forEach((year) => {
    const yearId = `${profileId}:${year}`;
    yearIdsByYear.set(year, yearId);
    yearsStore.put({
      id: yearId,
      profileId,
      year,
      archivedAt: year?.archivedAt ?? null,
      createdAt: now,
      updatedAt: now
    });
  });

  backup.budgetCategories.forEach((category) => {
    const year = String(category?.year ?? "").trim();

    if (!yearIdsByYear.has(year)) {
      return;
    }

    const nextId = crypto.randomUUID();
    const name = String(category?.name ?? "").trim();
    categoryIdsByLegacyId.set(String(category?.id ?? ""), nextId);
    categoryIdsByYearName.set(`${year}::${name.toLocaleLowerCase()}`, nextId);

    budgetCategoriesStore.put({
      id: nextId,
      profileId,
      year,
      name,
      ongoingBudget: Number(category?.ongoingBudget ?? 0),
      irregularBudget: Number(category?.irregularBudget ?? 0),
      archivedAt: category?.archivedAt ?? null,
      createdAt: category?.createdAt ?? now,
      updatedAt: category?.updatedAt ?? now
    });
  });

  backup.expenses.forEach((expense) => {
    restoreExpenseRecord({
      categoryIdsByLegacyId,
      categoryIdsByYearName,
      expense,
      expensesStore,
      now,
      profileId,
      yearIdsByYear
    });
  });

  await transactionDone(transaction);

  const scope = options.activate === false
    ? { profileId, year: backup.activeYear }
    : await setActiveBudgetScope(profileId, backup.activeYear);

  return {
    scope,
    profile: normalizeProfileRecord({
      id: profileId,
      name: profileName,
      source: options.source ?? "restore",
      isDefault: false,
      archivedAt: null,
      createdAt: now,
      updatedAt: now
    }),
    appSettings: backup.appSettings
  };
}
